import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface Notification {
  id: string;
  message: string;
  type: NotificationType;
}

interface NotificationContextType {
  showNotification: (message: string, type?: NotificationType) => void;
  showConfirm: (message: string, onConfirm: () => void, onCancel?: () => void) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const NOTIFICATION_STYLES: Record<
  NotificationType,
  {
    icon: string;
    badge: string;
  }
> = {
  success: {
    icon: 'text-emerald-300',
    badge: 'from-emerald-500/15 to-emerald-400/5 border-emerald-400/40'
  },
  error: {
    icon: 'text-rose-300',
    badge: 'from-rose-500/15 to-rose-400/5 border-rose-400/40'
  },
  warning: {
    icon: 'text-amber-300',
    badge: 'from-amber-500/15 to-amber-400/5 border-amber-400/40'
  },
  info: {
    icon: 'text-sky-300',
    badge: 'from-sky-500/15 to-sky-400/5 border-sky-400/40'
  }
};

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [confirmModal, setConfirmModal] = useState<{
    message: string;
    onConfirm: () => void;
    onCancel?: () => void;
  } | null>(null);

  const showNotification = useCallback((message: string, type: NotificationType = 'info') => {
    const id = Date.now().toString();
    setNotifications((prev) => [...prev, { id, message, type }]);

    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 4000);
  }, []);

  const showConfirm = useCallback(
    (message: string, onConfirm: () => void, onCancel?: () => void) => {
      setConfirmModal({ message, onConfirm, onCancel });
    },
    []
  );

  const handleConfirm = () => {
    if (confirmModal) {
      confirmModal.onConfirm();
      setConfirmModal(null);
    }
  };

  const handleCancel = () => {
    if (confirmModal?.onCancel) {
      confirmModal.onCancel();
    }
    setConfirmModal(null);
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const getIcon = (type: NotificationType) => {
    const iconClass = `w-5 h-5 ${NOTIFICATION_STYLES[type].icon}`;
    switch (type) {
      case 'success':
        return <CheckCircle className={iconClass} strokeWidth={2.2} />;
      case 'error':
        return <AlertCircle className={iconClass} strokeWidth={2.2} />;
      case 'warning':
        return <AlertTriangle className={iconClass} strokeWidth={2.2} />;
      default:
        return <Info className={iconClass} strokeWidth={2.2} />;
    }
  };

  return (
    <NotificationContext.Provider value={{ showNotification, showConfirm }}>
      {children}
      
      {/* Toast Notifications */}
      <div className="fixed top-[max(0.75rem,var(--safe-area-inset-top))] right-[max(0.75rem,var(--safe-area-inset-right))] z-50 flex flex-col gap-2.5 sm:gap-3 max-w-[calc(100vw-1.5rem)] sm:max-w-md w-full sm:w-auto pointer-events-none">
        {notifications.map((notification, index) => (
          <div
            key={notification.id}
            role="status"
            aria-live="polite"
            className="relative overflow-hidden rounded-2xl p-3 sm:p-3.5 shadow-[0_18px_48px_-24px_rgba(15,23,42,0.75)] border border-white/15 bg-gradient-to-br from-slate-900/95 via-slate-900/92 to-slate-950/92 backdrop-blur-md flex items-start gap-3 pointer-events-auto animate-toast-in"
            style={{ animationDelay: `${index * 45}ms` }}
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white/15" />

            <div className={`flex-shrink-0 w-8 h-8 rounded-xl border bg-gradient-to-br ${NOTIFICATION_STYLES[notification.type].badge} flex items-center justify-center shadow-[inset_0_1px_0_rgba(255,255,255,0.22)]`}>
              <div className="scale-105">
                {getIcon(notification.type)}
              </div>
            </div>

            <div className="flex-1 min-w-0 self-center">
              <p className="text-white text-sm sm:text-[15px] font-medium leading-snug">
                {notification.type === 'error' && <span className="sr-only">Error: </span>}
                {notification.message}
              </p>
            </div>

            <button
              onClick={() => removeNotification(notification.id)}
              className="flex-shrink-0 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 active:bg-white/15 transition-colors p-1.5 -mt-1 -mr-1 touch-manipulation"
              aria-label="Cerrar notificación"
            >
              <X className="w-[18px] h-[18px] sm:w-4 sm:h-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Confirm Modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 p-3 sm:p-4 flex items-end sm:items-center justify-center bg-slate-950/78">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Confirmar acción"
            className="relative overflow-hidden w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 text-slate-100 shadow-[0_24px_70px_-30px_rgba(2,6,23,0.9)]"
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-slate-500/50" />
            <div className="p-4 sm:p-5">
              <div className="flex items-start gap-3.5 mb-5">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl border border-amber-300/35 bg-gradient-to-br from-amber-500/15 to-amber-400/5 flex items-center justify-center shadow-[inset_0_1px_0_rgba(255,255,255,0.22)]">
                  <AlertTriangle className="w-5 h-5 text-amber-300" strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-1">
                    Confirmar acción
                  </h3>
                  <p className="text-sm text-slate-300/90 leading-relaxed">
                    {confirmModal.message}
                  </p>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-3 sm:justify-end">
                <button
                  onClick={handleCancel}
                  className="px-4 py-3 sm:py-2.5 rounded-xl border border-slate-600 text-slate-100 bg-slate-800 hover:bg-slate-700 active:bg-slate-600 transition-colors font-medium text-sm touch-manipulation"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirm}
                  className="px-4 py-3 sm:py-2.5 rounded-xl text-white bg-gradient-to-r from-rose-600 to-rose-500 hover:from-rose-500 hover:to-rose-400 active:from-rose-700 active:to-rose-600 transition-colors font-semibold text-sm shadow-[0_10px_24px_-12px_rgba(244,63,94,0.8)] touch-manipulation"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}
